<template>
  <component
    :is="tag"
    :class="[
      'b-custom-header',
      $style.header,
      padding && $style.headerPadding,
      sticky && $style.headerSticky,
    ]"
  >
    <div :class="[
      'b-custom-header_left',
      hasIcon ? $style.hasIcon : '',
      ellipsis && $style.hasIconEllipsis,
      $style.left
    ]">
      <button v-if="hasIcon" data-testid="b-custom-header-icon-back" @click="$emit('icon-click')" :class="[$style.icon, !ellipsis && $style.iconNoEllipsis]">
        <component
          :is="iconComponent"
          :size="24"
          :name="icon"
          :rotate="iconRotate"
        />
      </button>
      <div class="min-w-0 flex-1">
        <div
          :class="[$style.headerTitle, ellipsis && 'txt--ellipsis']"
          :data-testid="getTestId"
        >
          <span
            :class="[$style.title, ellipsis && $style.wordBreakAll]"
            :style="{fontSize}"
          >
            {{ title }}
          </span>
          <span
            v-if="subtitle"
            :class="$style.titleSeparator"
          >
            •
          </span>
        </div>
        <div v-if="description" :class="[$style.description]">{{ description }}</div>
      </div>
      <span
        v-if="subtitle"
        :class="$style.title"
        data-testid="b-custom-header-subtitle"
      >
        {{ subtitle }}
      </span>
      <b-hint
        v-if="hintDescription"
        :class="$style.hint"
        name="help"
        :height="hintHeight"
        :description="hintDescription"
        :position="hintPosition"
      />
    </div>
    <div :class="['b-custom-header_right', $style.right]">
      <slot/>
    </div>
  </component>
</template>

<script>
import { onMounted } from 'vue'

export default {
  emits: [
    'icon-click',
    'mounted',
  ],
  props: {
    name: {
      type: String,
      default: '',
    },

    ellipsis: {
      type: Boolean,
      default: true,
    },

    title: {
      type: String,
      required: true,
    },

    description: {
      type: String,
      required: false,
    },

    padding: Boolean,

    sticky: Boolean,

    subtitle: String,

    hasIcon: {
      type: Boolean,
      default: true,
    },

    hintHeight: {
      type: String,
      default: '23px',
    },

    hintDescription: {
      type: String,
      default: '',
    },

    hintPosition: {
      type: String,
      default: 'right-bottom',
    },

    icon: {
      type: String,
      default: 'x',
    },

    iconRotate: {
      type: String,
      required: false,
    },

    tag: {
      type: String,
      default: 'header',
      validator: (v) => ['header', 'div'].includes(v),
    },

    fontSize: {
      type: String,
      required: false,
    },

    iconComponent: {
      type: String,
      default: 'b-svg',
    },
  },

  setup (_, { emit }) {
    onMounted(() => {
      emit('mounted')
    })
  },

  computed: {
    getTestId () {
      return `${this.name ? `${this.name}-` : ''}header`
    },
  },
}
</script>

<style lang="scss" module>

.header {
  display: flex;
  flex-flow: row wrap;

  .left {
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: flex-start;
    max-width: 100%;
    min-height: $header-height;
    padding-right: 1.5rem;
    min-width: 0;
  }

  .right {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: flex-end;
    margin-left: auto;
    align-self: flex-start;
  }

  &Title {
    max-width: 630px;
    min-width: 0;
  }

  &Padding {
    padding: var(--content-padding) 0;
  }

  &Sticky {
    position: sticky;
    top: 0;
    background: $color-body-bg;
    z-index: 1000;
  }

  .hasIcon {
    flex-direction: row;
    align-items: flex-start;
  }

  .hasIconEllipsis {
    align-items: center;
  }
}

.icon {
  padding: .75rem 1rem .75rem 0;
  align-self: flex-start;
  display: flex;
}

.iconNoEllipsis {
  margin-top: 5px;
}

.title {
  font-weight: var(--b-font-weight-headings);
  font-size: var(--b-font-size-h2);
  line-height: var(--b-line-height-h2);

  @include media-breakpoint-down($breakpoint-mobile) {
    font-size: var(--b-font-size-h3);
  }

  &Separator {
    padding: 0 12px;
    margin-top: -4px;
  }
}

.description {
  color: var(--b-color-secondary);
}

.wordBreakAll {
  word-break: break-all;
}

.hint {
  color: $color-primary;
  cursor: pointer;
  margin: 3px 0 0 12px;
}
</style>
